import React from 'react';
import { Box, Typography } from '@mui/material';
import { DS } from '../../PatientForms/designTokens';
import { Headache } from '../types';
import { toggleExclusive } from '../toggleExclusive';

interface HasMultipleHeadachesProps {
  additionalHeadaches: Headache[];
  setAdditionalHeadaches: (headaches: Headache[]) => void;
  handleFormSubmit: (e: React.FormEvent) => void;
}
const HasMultipleHeadaches: React.FC<HasMultipleHeadachesProps> = ({
  additionalHeadaches,
  setAdditionalHeadaches,
  handleFormSubmit,
}) => {
  const handleInputChangeHeadache = (
    index: number,
    field: keyof Headache,
    value: string | string[]
  ) => {
    const updated = [...additionalHeadaches];
    const updatedHeadache = { ...updated[index] };

    if (
      field === 'associatedSymptomHeadaches' ||
      field === 'auraSymptomsHeadache' ||
      field === 'triggersHeadache'
    ) {
      updatedHeadache[field] = Array.isArray(value) ? value : [value];
    } else {
      updatedHeadache[field] = value as string;
    }

    updated[index] = updatedHeadache;
    setAdditionalHeadaches(updated);
  };

  const handleAddNewSet = () => {
    setAdditionalHeadaches([
      ...additionalHeadaches,
      {
        ageFirstAttackHeadache: '',
        headacheDurationHeadache: '',
        painLocationHeadache: '',
        attackPatternHeadache: '',
        attackPatternDuringHeadache: '',
        physicalActivityEffectHeadache: '',
        dizzinessDurationHeadache: '',
        painCharacterHeadache: '',
        preferredActionDuringAttackHeadache: '',
        painIntensityHeadache: '',
        associatedSymptomHeadaches: [],
        auraSymptomsHeadache: [],
        dizzinessTypeHeadache: '',
        headacheFrequencyHeadache: '',
        initialFrequencyHeadache: '',
        frequencyDurationHeadache: '',
        worseningOverTimeHeadache: '',
        worseningDurationHeadache: '',
        triggersHeadache: [],
      },
    ]);
  };

  const count = 2;

  return (
    <>
      {additionalHeadaches.map((headache, index) => (
        // eslint-disable-next-line react/no-array-index-key -- entries have no id; the list is only appended to and edited in place
        <Box key={index}>
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 600,
                color: DS.deepTeal,
                mb: 3,
                fontFamily: DS.fontDisplay,
              }}
            >
              Huvudvärk {count + index}
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Vid vilken ålder hade du ditt första anfall?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                '0-10 år',
                '11-20 år',
                '21-30 år',
                '31-40 år',
                '41-50 år',
                'mer än 50 år',
              ].map((ageFirstAttackHeadache) => (
                <Box
                  component="label"
                  key={ageFirstAttackHeadache}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="radio"
                    onBlur={handleFormSubmit}
                    name={`ageFirstAttackHeadache${index}`}
                    value={ageFirstAttackHeadache}
                    checked={
                      headache.ageFirstAttackHeadache === ageFirstAttackHeadache
                    }
                    onChange={(e) =>
                      handleInputChangeHeadache(
                        index,
                        'ageFirstAttackHeadache',
                        e.target.value
                      )
                    }
                    style={{ marginRight: '8px' }}
                  />
                  {ageFirstAttackHeadache}
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Hur länge varar ett obehandlat anfall?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                'mindre än 1 timme',
                '1-3 timmar',
                '4-72 timmar',
                'mer än 72 timmar',
              ].map((headacheDurationHeadache) => (
                <Box
                  component="label"
                  key={headacheDurationHeadache}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="radio"
                    onBlur={handleFormSubmit}
                    name={`headacheDurationHeadache${index}`}
                    value={headacheDurationHeadache}
                    checked={
                      headache.headacheDurationHeadache ===
                      headacheDurationHeadache
                    }
                    onChange={() =>
                      handleInputChangeHeadache(
                        index,
                        'headacheDurationHeadache',
                        headacheDurationHeadache
                      )
                    }
                    style={{ marginRight: '8px' }}
                  />
                  {headacheDurationHeadache}
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Var sitter vanligtvis smärtan?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                'i hela huvudet',
                'i halva huvudet',
                'som ett band runt huvudet',
                'i nacken/bakhuvudet',
                'i en specifik punkt eller litet område av huvudet',
                'i eller runt ena ögat eller i tinningen nära ena ögat',
              ].map((location) => (
                <Box
                  component="label"
                  key={location}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="radio"
                    onBlur={handleFormSubmit}
                    name={`painLocationHeadache${index}`}
                    value={location}
                    checked={headache.painLocationHeadache === location}
                    onChange={() =>
                      handleInputChangeHeadache(
                        index,
                        'painLocationHeadache',
                        location
                      )
                    }
                    style={{ marginRight: '8px' }}
                  />
                  {location}
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Har du denna huvudvärk från minst en attack varannan dag upp till
              8 ggr dagligen?
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  name={`attackPatternHeadache${index}`}
                  value="yes"
                  checked={headache.attackPatternHeadache === 'yes'}
                  onChange={() =>
                    handleInputChangeHeadache(
                      index,
                      'attackPatternHeadache',
                      'yes'
                    )
                  }
                  style={{ marginRight: '8px' }}
                />
                Ja
              </Box>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  name={`attackPatternHeadache${index}`}
                  value="no"
                  checked={headache.attackPatternHeadache === 'no'}
                  onChange={() => {
                    const updated = [...additionalHeadaches];
                    const updatedHeadache = { ...updated[index] };

                    updatedHeadache.attackPatternHeadache = 'no';
                    updatedHeadache.attackPatternDuringHeadache = '';

                    updated[index] = updatedHeadache;
                    setAdditionalHeadaches(updated);
                  }}
                  style={{ marginRight: '8px' }}
                />
                Nej
              </Box>
            </Box>
            {headache.attackPatternHeadache === 'yes' && (
              <Box>
                <br />
                <Typography
                  sx={{
                    display: 'block',
                    color: '#374151',
                    mb: 1,
                    fontFamily: DS.fontBody,
                    fontSize: '16px',
                  }}
                >
                  Har du dessa attacker under:
                </Typography>
                <Box component="label">
                  <input
                    type="radio"
                    onBlur={handleFormSubmit}
                    name={`attackPatternDuringHeadache${index}`}
                    value="yes"
                    checked={headache.attackPatternDuringHeadache === 'yes'}
                    onChange={() =>
                      handleInputChangeHeadache(
                        index,
                        'attackPatternDuringHeadache',
                        'yes'
                      )
                    }
                    style={{ marginRight: '8px' }}
                  />
                  Perioder av veckor-månader, sedan besvärsfrihet under
                  månader-år innan ny period börjar.
                </Box>
                <br />
                <Box component="label">
                  <input
                    type="radio"
                    onBlur={handleFormSubmit}
                    name={`attackPatternDuringHeadache${index}`}
                    value="yes"
                    checked={headache.attackPatternDuringHeadache === 'no'}
                    onChange={() =>
                      handleInputChangeHeadache(
                        index,
                        'attackPatternDuringHeadache',
                        'no'
                      )
                    }
                    style={{ marginRight: '8px' }}
                  />
                  Kontinuerligt sedan minst ett år tillbaka, möjligen med
                  uppehåll under mindre än 3 månader
                </Box>
              </Box>
            )}
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Vilken smärtkaraktär stämmer bäst med din huvudvärk?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                'pulserande/dunkande',
                'molande',
                'skärande',
                'brännande/svidande',
                'huggande',
                'tryckande',
              ].map((character) => (
                <Box
                  component="label"
                  key={character}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="radio"
                    onBlur={handleFormSubmit}
                    name={`painCharacterHeadache${index}`}
                    value={character}
                    checked={headache.painCharacterHeadache === character}
                    onChange={() =>
                      handleInputChangeHeadache(
                        index,
                        'painCharacterHeadache',
                        character
                      )
                    }
                    style={{ marginRight: '8px' }}
                  />
                  {character}
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Hur uttalad är smärtan när den är som värst?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {['lätt', 'måttlig', 'intensiv'].map((intensity) => (
                <Box
                  component="label"
                  key={intensity}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="radio"
                    onBlur={handleFormSubmit}
                    name={`painIntensityHeadache${index}`}
                    value={intensity}
                    checked={headache.painIntensityHeadache === intensity}
                    onChange={() =>
                      handleInputChangeHeadache(
                        index,
                        'painIntensityHeadache',
                        intensity
                      )
                    }
                    style={{ marginRight: '8px' }}
                  />
                  {intensity}
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Under pågående anfall, förvärras då smärtan av normal fysisk
              aktivitet, som t.ex gång i trappor?
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  name={`physicalActivityEffectHeadache${index}`}
                  value="yes"
                  checked={headache.physicalActivityEffectHeadache === 'yes'}
                  onChange={() =>
                    handleInputChangeHeadache(
                      index,
                      'physicalActivityEffectHeadache',
                      'yes'
                    )
                  }
                  style={{ marginRight: '8px' }}
                />
                Ja
              </Box>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  name={`physicalActivityEffectHeadache${index}`}
                  value="no"
                  checked={headache.physicalActivityEffectHeadache === 'no'}
                  onChange={() =>
                    handleInputChangeHeadache(
                      index,
                      'physicalActivityEffectHeadache',
                      'no'
                    )
                  }
                  style={{ marginRight: '8px' }}
                />
                Nej
              </Box>
            </Box>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Om du har fria möjligheter, vad gör du då helst under ett anfall?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                'Lägger mig ned i ett mörkt och tyst rum',
                'Tar en promenad',
                'Sätter mig ned och tar det lugnt',
                'Går omkring då smärtan är svår att stå ut med eller förvärras i liggande',
              ].map((action) => (
                <Box
                  component="label"
                  key={action}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="radio"
                    onBlur={handleFormSubmit}
                    name={`preferredActionDuringAttackHeadache${index}`}
                    value={action}
                    checked={
                      headache.preferredActionDuringAttackHeadache === action
                    }
                    onChange={() =>
                      handleInputChangeHeadache(
                        index,
                        'preferredActionDuringAttackHeadache',
                        action
                      )
                    }
                    style={{ marginRight: '8px' }}
                  />
                  {action}
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Har du andra symptom utöver huvudvärk under ett anfall?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                'illamående/kräkningar',
                'överkänslighet för ljus',
                'överkänslighet för ljud',
                'överkänslighet för lukter',
                'Nej, inget av ovanstående',
              ].map((symptom) => (
                <Box
                  component="label"
                  key={symptom}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="checkbox"
                    onBlur={handleFormSubmit}
                    name={`associatedSymptomHeadaches${index}`}
                    value={symptom}
                    checked={headache.associatedSymptomHeadaches?.includes(
                      symptom
                    )}
                    onChange={() => {
                      handleInputChangeHeadache(
                        index,
                        'associatedSymptomHeadaches',
                        headache.associatedSymptomHeadaches.includes(symptom)
                          ? headache.associatedSymptomHeadaches.filter(
                              (item) => item !== symptom
                            )
                          : [...headache.associatedSymptomHeadaches, symptom]
                      );
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  {symptom}
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Har du något eller några av följande symptom strax innan eller
              under en huvudvärksattack?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                'synstörning (flimmer/blixtar i delar av synfältet)',
                'känselstörning (domning/stickning i ena sidans arm och/eller ben)',
                'svårighet att hitta ord',
                'sluddrigt tal',
                'svaghet i ena sidans arm och/eller ben',
                'yrsel',
                'hängande ögonlock och/eller mindre pupill på samma sida som huvudvärken',
                'svullet ögonlock på samma sida som huvudvärken',
                'ögonrodnad och/eller ökat tårflöde på samma sida som huvudvärken',
                'rinnande näsa eller nästäppa på samma sida som huvudvärken',
                'svettning i pannan och ansiktet på samma sida som huvudvärken',
                'Inget av ovanstående',
              ].map((aura) => (
                <Box
                  component="label"
                  key={aura}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="checkbox"
                    name={`auraSymptomsHeadache${index}`}
                    onBlur={handleFormSubmit}
                    value={aura}
                    checked={headache.auraSymptomsHeadache?.includes(aura)}
                    onChange={() => {
                      handleInputChangeHeadache(
                        index,
                        'auraSymptomsHeadache',
                        headache.auraSymptomsHeadache.includes(aura)
                          ? headache.auraSymptomsHeadache.filter(
                              (item) => item !== aura
                            )
                          : [...headache.auraSymptomsHeadache, aura]
                      );
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  {aura}
                </Box>
              ))}
            </Box>
          </Box>
          {headache.auraSymptomsHeadache.includes('yrsel') && (
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Hur skulle du beskriva din yrsel?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {[
                  'snurrande som när man åker karusell',
                  'gungande som på en båt',
                  'allmän olustkänsla',
                  'inget av ovanstående',
                ].map((dizziness) => (
                  <Box
                    component="label"
                    key={dizziness}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <input
                      type="radio"
                      onBlur={handleFormSubmit}
                      name={`dizzinessTypeHeadache${index}`}
                      value={dizziness}
                      checked={headache.dizzinessTypeHeadache === dizziness}
                      onChange={() =>
                        handleInputChangeHeadache(
                          index,
                          'dizzinessTypeHeadache',
                          dizziness
                        )
                      }
                      style={{ marginRight: '8px' }}
                    />
                    {dizziness}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {headache.auraSymptomsHeadache.some(
            (item) => item !== 'Inget av ovanstående'
          ) && (
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Hur länge varar vart och ett av dessa symptom?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {[
                  'mindre än 5 min',
                  '5-15 min',
                  '15-60 min',
                  '1-4 timmar',
                  'mer än 4 timmar',
                ].map((duration) => (
                  <Box
                    component="label"
                    key={duration}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <input
                      type="radio"
                      onBlur={handleFormSubmit}
                      name={`dizzinessDurationHeadache${index}`}
                      value={duration}
                      checked={headache.dizzinessDurationHeadache === duration}
                      onChange={() =>
                        handleInputChangeHeadache(
                          index,
                          'dizzinessDurationHeadache',
                          duration
                        )
                      }
                      style={{ marginRight: '8px' }}
                    />
                    {duration}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Ungefär hur ofta har du ovan beskrivna huvudvärk?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                'mindre än en gång per år',
                '1-11 gånger per år',
                '1-3 ggr per månad',
                '4-7 ggr per månad',
                '8-14 ggr per månad',
                '15-29 ggr per månad',
                'dagligen',
              ].map((frequency) => (
                <Box
                  component="label"
                  key={frequency}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="radio"
                    name={`headacheFrequencyHeadache${index}`}
                    onBlur={handleFormSubmit}
                    value={frequency}
                    checked={headache.headacheFrequencyHeadache === frequency}
                    onChange={() =>
                      handleInputChangeHeadache(
                        index,
                        'headacheFrequencyHeadache',
                        frequency
                      )
                    }
                    style={{ marginRight: '8px' }}
                  />
                  {frequency}
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Sedan hur länge har du haft denna huvudvärksfrekvens?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                'mindre än 1 månad',
                '1-3 månader',
                '3-6 månader',
                '6-12 månader',
                '1-2 år',
                '2-5 år',
                '5-10 år',
                '10-20 år',
                'mer än 20 år',
                'sedan debuten',
              ].map((duration) => (
                <Box
                  component="label"
                  key={duration}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="radio"
                    onBlur={handleFormSubmit}
                    name={`frequencyDurationHeadache${index}`}
                    value={duration}
                    checked={headache.frequencyDurationHeadache === duration}
                    onChange={() =>
                      handleInputChangeHeadache(
                        index,
                        'frequencyDurationHeadache',
                        duration
                      )
                    }
                    style={{ marginRight: '8px' }}
                  />
                  {duration}
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Hur ofta hade du denna typ av huvudvärk de första åren efter
              debuten?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                'mindre än en gång per år',
                '1-11 gånger per år',
                '1-3 ggr per månad',
                '4-7 ggr per månad',
                '8-14 ggr per månad',
                '15-29 ggr per månad',
                'dagligen',
              ].map((frequency) => (
                <Box
                  component="label"
                  key={frequency}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="radio"
                    onBlur={handleFormSubmit}
                    name={`initialFrequencyHeadache${index}`}
                    value={frequency}
                    checked={headache.initialFrequencyHeadache === frequency}
                    onChange={() =>
                      handleInputChangeHeadache(
                        index,
                        'initialFrequencyHeadache',
                        frequency
                      )
                    }
                    style={{ marginRight: '8px' }}
                  />
                  {frequency}
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Har huvudvärken försämrats med tiden?
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  name={`worseningOverTimeHeadache${index}`}
                  value="yes"
                  checked={headache.worseningOverTimeHeadache === 'yes'}
                  onChange={() =>
                    handleInputChangeHeadache(
                      index,
                      'worseningOverTimeHeadache',
                      'yes'
                    )
                  }
                  style={{ marginRight: '8px' }}
                />
                Ja
              </Box>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  name={`worseningOverTimeHeadache${index}`}
                  value="no"
                  checked={headache.worseningOverTimeHeadache === 'no'}
                  onChange={() =>
                    handleInputChangeHeadache(
                      index,
                      'worseningOverTimeHeadache',
                      'no'
                    )
                  }
                  style={{ marginRight: '8px' }}
                />
                Nej
              </Box>
            </Box>
            {headache.worseningOverTimeHeadache === 'yes' && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  sx={{
                    display: 'block',
                    color: '#374151',
                    mb: 1,
                    fontFamily: DS.fontBody,
                    fontSize: '16px',
                  }}
                >
                  Hur länge är det sedan din huvudvärk försämrades?
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {[
                    'mindre än 1 månad',
                    '1-3 månader',
                    '3-6 månader',
                    '6-12 månader',
                    '1-2 år',
                    '2-5 år',
                    '5-10 år',
                    '10-20 år',
                    'mer än 20 år',
                  ].map((duration) => (
                    <Box
                      component="label"
                      key={duration}
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <input
                        type="radio"
                        onBlur={handleFormSubmit}
                        name={`worseningDurationHeadache${index}`}
                        value={duration}
                        checked={
                          headache.worseningDurationHeadache === duration
                        }
                        onChange={() =>
                          handleInputChangeHeadache(
                            index,
                            'worseningDurationHeadache',
                            duration
                          )
                        }
                        style={{ marginRight: '8px' }}
                      />
                      {duration}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Triggas din huvudvärk av någon eller några följande faktorer?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                'menstruation och/eller ägglossning',
                'födoämne (t ex choklad, starka ostar, citrusfrukter)',
                'alkohol',
                'stress',
                'solljus',
                'starka lukter',
                'träning',
                'inget av ovanstående',
              ].map((trigger) => (
                <Box
                  component="label"
                  key={trigger}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="checkbox"
                    onBlur={handleFormSubmit}
                    name={`triggersHeadache${index}`}
                    value={trigger}
                    checked={headache.triggersHeadache?.includes(trigger)}
                    onChange={(e) => {
                      const { value } = e.target;
                      handleInputChangeHeadache(
                        index,
                        'triggersHeadache',
                        toggleExclusive(
                          headache.triggersHeadache ?? [],
                          value,
                          'inget av ovanstående'
                        )
                      );
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  {trigger}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      ))}
      <Box
        component="button"
        onClick={handleAddNewSet}
        sx={{
          mt: 2,
          py: 1.5,
          px: 2,
          backgroundColor: DS.deepTeal,
          color: '#FFFFFF',
          borderRadius: '6px',
          width: '100%',
          border: 'none',
          cursor: 'pointer',
          fontFamily: DS.fontBody,
          fontSize: '16px',
        }}
      >
        Lägg till ytterligare huvudvärk
      </Box>
    </>
  );
};

export default HasMultipleHeadaches;
